'use client';

import { ChevronDown } from 'lucide-react';
import React from 'react';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';

export type Option = {
    label: string;
    value: string;
    disabled?: boolean;
};

type Props = {
    options: Option[];
    placeHolder: string;
    customClass?: string;
    styleClass?: string;
    selectedValues: string[];
    onValueChange: (values: string[]) => void;
    onBlur?: (values: string[]) => void;
    minValue?: number;
};

export default function MultiSelectDropdown({
    options,
    placeHolder,
    customClass,
    styleClass = 'py-[13px] pl-[20px] pr-[14px] bg-[rgba(255,255,255,0.10)] rounded-[8px] border border-white',
    selectedValues,
    onValueChange,
    onBlur = () => {},
    minValue = 0,
}: Props) {
    const toggleValue = (value: string) => {
        const isCurrentlySelected = selectedValues.includes(value);

        if (isCurrentlySelected) {
            if (selectedValues.length <= minValue) return;
            const newValues = selectedValues.filter((v) => v !== value);
            onValueChange(newValues);
        } else {
            const newValues = [...selectedValues, value];
            onValueChange(newValues);
        }
    };

    return (
        <div className={customClass}>
            <DropdownMenu
                onOpenChange={(open) => {
                    if (!open && onBlur) onBlur(selectedValues);
                }}
            >
                <DropdownMenuTrigger asChild>
                    <div
                        className={`
                        flex items-center justify-between
                        gap-2 w-full
                        text-sm text-white font-normal
                        ${styleClass}
                        hover:bg-transparent
                        focus:outline-none focus:ring-0 focus-visible:ring-0`}
                    >
                        <span className="flex-1">{placeHolder}</span>
                        <ChevronDown className="ml-2 h-6 w-6 text-white" />
                    </div>
                </DropdownMenuTrigger>

                <DropdownMenuContent className="w-64 max-h-[410px] overflow-y-auto">
                    {options.map((option) => (
                        <DropdownMenuCheckboxItem
                            key={option.value}
                            disabled={option.disabled}
                            checked={selectedValues.includes(option.value)}
                            onCheckedChange={() => toggleValue(option.value)}
                            onSelect={(e) => e.preventDefault()}
                            className="relative flex items-center gap-2 p-1 m-3 rounded-md hover:bg-gray-100 focus:outline-none"
                        >
                            <div className="w-[16px] h-[16px] rounded-[4px] border-[1px] border-[#C4C4C4] bg-white peer-checked:bg-pink-500 flex items-center justify-center">
                                <div
                                    className={`w-full h-full rounded-[3px] transition-all duration-200 ${
                                        selectedValues.includes(option.value) ? 'bg-pink-500' : 'bg-white'
                                    }`}
                                />
                            </div>
                            {option.label}
                        </DropdownMenuCheckboxItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}
